import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { hydrateQueryClient, attachQueryPersist } from '@/lib/queryPersist';
import './index.css';

// Force dark theme — light theme removed in Phase 21.1.
document.documentElement.classList.add('dark');
document.documentElement.classList.remove('light');

// Register service worker for web push + offline cache.
// Fire-and-forget; failures are logged only.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        console.log('[SW] registered, scope:', reg.scope);
      })
      .catch((err) => {
        console.warn('[SW] registration failed:', err);
      });
  });
}

// Ask the browser to keep our cached media around even when disk is tight.
// Without this, the SW's media cache is "best effort" and may be evicted
// under storage pressure. With granted persistence, Chrome treats us as
// important and never silently clears the data.
if ('storage' in navigator && 'persist' in navigator.storage) {
  navigator.storage.persist().then((granted) => {
    console.log('[storage] persistent:', granted);
  }).catch(() => {});
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // gcTime должен быть ≥ возраста снапшота, иначе гидрированные записи
      // сразу собираются GC'ом прежде чем UI их успевает прочитать.
      gcTime: 24 * 60 * 60 * 1000,
      retry: (failureCount, error: unknown) => {
        const status = (error as { response?: { status: number } })?.response?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
    },
  },
});

// v5.7.8 — для messages-семейства queries: always fresh.
// Дневной snapshot из IDB не должен залипать в UI; даже если cache есть,
// каждый mount/focus/reconnect должен force-refetch. Trade-off: больше
// REST-запросов на /api/messages/channel/:id, но UX-correctness >
// network-savings.
// Семьи queryKeys (по убыванию специфичности):
//   ['messages']                              — root
//   ['messages', 'channel', channelId, ...]   — channel messages
//   ['messages', 'dm', peerId, ...]           — dm history
// setQueryDefaults применяется ко всем queries чей queryKey НАЧИНАЕТСЯ с ['messages'].
queryClient.setQueryDefaults(['messages'], {
  staleTime: 0,
  refetchOnMount: 'always',
  refetchOnWindowFocus: 'always',
  refetchOnReconnect: 'always',
});

// Phase 15.2 — гидрируем query-cache из IDB, потом подписываемся на изменения
// и фоном сохраняем обратно. UI стартует с тёплым кэшем.
// Phase 15.3 — сверяем snapshot.userId с текущим (защита от смены аккаунта
// на общем устройстве); snapshot бьётся если кто-то сменил логин.
import { useAuthStore } from '@/store/authStore';
const currentUserId = useAuthStore.getState().user?.id ?? null;
void hydrateQueryClient(queryClient, currentUserId).finally(() => {
  attachQueryPersist(queryClient, () => useAuthStore.getState().user?.id ?? null);
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>,
);
